import type { ArtisanJson } from './artisanParser';

/**
 * MurmurHash3 (x86, 32-bit). Deterministic and dependency-free — unlike `crypto.subtle`,
 * which is unavailable outside a secure context and would force this path to be async.
 * Two UTF-16 code units are packed per block so non-ASCII metadata still contributes.
 */
const murmur3 = (input: string, seed: number): number => {
    const c1 = 0xcc9e2d51;
    const c2 = 0x1b873593;
    let h = seed >>> 0;
    let i = 0;

    for (; i + 2 <= input.length; i += 2) {
        let k = (input.charCodeAt(i) | (input.charCodeAt(i + 1) << 16)) >>> 0;
        k = Math.imul(k, c1);
        k = (k << 15) | (k >>> 17);
        k = Math.imul(k, c2);
        h ^= k;
        h = (h << 13) | (h >>> 19);
        h = (Math.imul(h, 5) + 0xe6546b64) >>> 0;
    }

    // Tail: a trailing odd code unit.
    if (i < input.length) {
        let k = input.charCodeAt(i);
        k = Math.imul(k, c1);
        k = (k << 15) | (k >>> 17);
        k = Math.imul(k, c2);
        h ^= k;
    }

    h ^= input.length;
    h ^= h >>> 16;
    h = Math.imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
    return h >>> 0;
};

/**
 * 128-bit content digest built from four independently seeded hashes, so two distinct
 * roasts colliding is not a practical concern.
 */
export const fingerprintFileContent = (text: string): string =>
    [0x9747b28c, 0x85ebca6b, 0xc2b2ae35, 0x27d4eb2f]
        .map(seed => murmur3(text, seed).toString(16).padStart(8, '0'))
        .join('');

/**
 * Stable identity for a roast, used by the backend to reject re-uploads of the same file
 * (unique index on `artisan_profiles(company_id, roast_uuid)`).
 *
 * Artisan stamps every export with a `roastUUID`, which survives re-exports of the same
 * roast and is therefore the better key. Older exports and hand-built JSON have none, so
 * those fall back to a digest of the file's raw bytes — namespaced to keep it from ever
 * being mistaken for an Artisan-issued UUID.
 */
export const buildRoastIdentity = (json: ArtisanJson, rawText: string): string => {
    const uuid = json.roastUUID?.trim();
    return uuid ? uuid : `file:${fingerprintFileContent(rawText)}`;
};
